// src/app/pages/app/RequestsPage.jsx
import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTradeRequests } from "../../features/trade/useTradeRequests.js";
import { getCompletedTrades, getCompletedTradesSummary } from "../../features/trade/tradeApi.js";
import {
  ListChecks,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Award,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Wallet,
  Target,
} from "lucide-react";

function normalizeList(data, pageSizeFallback = 10) {
  if (!data) return { items: [], total: 0, limit: pageSizeFallback, offset: 0 };

  if (Array.isArray(data)) {
    return { items: data, total: data.length, limit: data.length || pageSizeFallback, offset: 0 };
  }

  const items = data.items || data.results || data.data || [];
  const total =
    typeof data.total === "number"
      ? data.total
      : typeof data.count === "number"
      ? data.count
      : items.length;

  const limit =
    typeof data.limit === "number"
      ? data.limit
      : typeof data.pageSize === "number"
      ? data.pageSize
      : typeof data.page_size === "number"
      ? data.page_size
      : pageSizeFallback;

  const offset = typeof data.offset === "number" ? data.offset : typeof data.skip === "number" ? data.skip : 0;

  return { items, total, limit, offset };
}

function Pill({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
      : tone === "bad"
      ? "bg-red-500/10 border-red-500/30 text-red-300"
      : tone === "warning"
      ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-300"
      : tone === "info"
      ? "bg-blue-500/10 border-blue-500/30 text-blue-300"
      : "bg-gray-500/10 border-gray-500/30 text-gray-300";
  return <span className={`px-2 py-1 text-xs rounded-full border ${cls}`}>{children}</span>;
}

function TradeStatusBadge({ status }) {
  const s = String(status || "UNKNOWN").toUpperCase();
  const statusMap = {
    'PENDING': { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
    'APPROVED': { label: 'Approved', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
    'REJECTED': { label: 'Rejected', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
    'RESOLVED': { label: 'Settled', className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
    'CANCELLED': { label: 'Cancelled', className: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
    'WIN': { label: 'Win', className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
    'LOSE': { label: 'Loss', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
    'PUSH': { label: 'Push', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  };
  
  const info = statusMap[s] || { label: s, className: 'bg-gray-500/20 text-gray-300 border-gray-500/30' };
  
  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${info.className}`}>
      {info.label}
    </span>
  );
}

function OutcomePill({ outcome }) {
  const s = String(outcome || "UNKNOWN").toUpperCase();
  const tone = s === "WIN" ? "good" : s === "LOSE" ? "bad" : s === "PUSH" ? "info" : "neutral";
  return <Pill tone={tone}>{s || "—"}</Pill>;
}

function formatMoney(x) {
  const n = Number(x || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

function toPercentFromMultiplier(mult) {
  const m = Number(mult);
  if (!Number.isFinite(m) || m <= 0) return "—";
  const p = (m - 1) * 100;
  return `${p.toFixed(0)}%`;
}

function directionLabel(dir) {
  const d = String(dir || "").toUpperCase();
  if (d === "UP") return { label: "Long", cls: "text-emerald-300" };
  if (d === "DOWN") return { label: "Short", cls: "text-red-300" };
  return { label: d || "—", cls: "text-gray-300" };
}

// ✅ NEW: Normalize win rate to always be a percentage (0-100)
function normalizeWinRate(rawRate, wins, losses) {
  const w = Number(wins || 0);
  const l = Number(losses || 0);
  const total = w + l;
  
  // ✅ Priority 1: Compute from wins/losses (most reliable)
  if (total > 0) {
    return Math.round((w / total) * 1000) / 10;
  }
  
  // Priority 2: Fall back to backend value
  const rate = Number(rawRate || 0);
  if (rate > 0 && rate <= 1) {
    // Backend returned decimal (0.4) → convert to percentage (40.0)
    return Math.round(rate * 1000) / 10;
  }
  return Math.round(rate * 10) / 10;
}

function CompletedCard({ r }) {
  const { label: dirLabel, cls: dirCls } = directionLabel(r.direction);

  const stake = r.stakeAmount ?? r.stake_amount ?? r.stake ?? 0;
  const locked = r.lockedAmount ?? r.locked_amount ?? stake;
  const pct = toPercentFromMultiplier(r.payoutMultiplier ?? r.payout_multiplier ?? r.multiplier);
  const entry = r.entryPrice ?? r.entry_price;
  const exit = r.exitPrice ?? r.exit_price;

  const approvedAt = r.approvedAt ?? r.approved_at;
  const resolvedAt = r.resolvedAt ?? r.resolved_at;
  const duration = r.durationSeconds ?? r.duration_seconds ?? r.duration ?? "—";

  const outcome = String(r.outcome || "—").toUpperCase();
  const note = r.note || r.adminNotes || r.admin_notes;

  const isWin = outcome === "WIN";

  return (
    <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 p-5 transition-all duration-300 hover:border-gray-700 hover:shadow-xl hover:shadow-black/30">
      {/* Decorative glow */}
      <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
        isWin ? 'bg-emerald-500/10' : 'bg-red-500/10'
      }`} />
      
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
              isWin 
                ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/20' 
                : 'bg-gradient-to-br from-red-500/20 to-rose-500/20 border border-red-500/20'
            }`}>
              {isWin ? (
                <TrendingUp size={18} className="text-emerald-400" />
              ) : (
                <TrendingDown size={18} className="text-red-400" />
              )}
            </div>
            <div>
              <div className="text-lg font-bold text-white">{r.market || r.symbol || "—"}</div>
              <div className="text-xs text-gray-400 flex items-center gap-2">
                <span className={dirCls}>{dirLabel}</span>
                <span className="text-gray-600">•</span>
                <span>{duration}s</span>
              </div>
            </div>
          </div>
          <OutcomePill outcome={outcome} />
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 rounded-xl bg-white/5">
            <div className="text-xs text-gray-400">Stake</div>
            <div className="text-sm font-semibold text-white">${formatMoney(stake)}</div>
          </div>
          <div className="p-3 rounded-xl bg-white/5">
            <div className="text-xs text-gray-400">Return</div>
            <div className="text-sm font-semibold text-white">{pct}</div>
          </div>
          <div className="p-3 rounded-xl bg-white/5">
            <div className="text-xs text-gray-400">Payout</div>
            <div className={`text-sm font-semibold ${isWin ? 'text-emerald-400' : 'text-gray-300'}`}>
              ${formatMoney(r.payoutAmount ?? r.payout_amount ?? 0)}
            </div>
          </div>
          <div className="p-3 rounded-xl bg-white/5">
            <div className="text-xs text-gray-400">Settled</div>
            <div className="text-sm font-semibold text-white">
              {resolvedAt ? new Date(resolvedAt).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>

        {note && (
          <div className="mt-3 p-3 rounded-xl bg-white/5 border border-gray-800">
            <div className="text-xs text-gray-400 mb-1">Note</div>
            <div className="text-sm text-gray-300">{note}</div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-800/50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            ID: <span className="font-mono text-gray-400">{String(r.id || "").slice(0, 12)}</span>
          </div>
          <div className="flex items-center gap-2">
            {entry && (
              <span className="text-xs text-gray-500">
                Entry: <span className="text-gray-300">${formatMoney(entry)}</span>
              </span>
            )}
            {exit && (
              <span className="text-xs text-gray-500">
                Exit: <span className="text-gray-300">${formatMoney(exit)}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingRow({ r }) {
  const market = r.market || r.symbol || "—";
  const direction = (r.direction || "—").toUpperCase();
  const stake = r.stakeAmount ?? r.stake_amount ?? r.stake ?? "—";
  const locked = r.lockedAmount ?? r.locked_amount ?? stake;
  const dur = r.durationSeconds ?? r.duration_seconds ?? r.duration ?? "—";
  const mult = r.payoutMultiplier ?? r.payout_multiplier ?? r.multiplier ?? "—";
  const status = (r.status || r.state || "PENDING").toUpperCase();
  const createdAt = r.created_at || r.createdAt || "";
  const note = r.note;

  const isUp = direction === "UP";

  return (
    <div className="group grid grid-cols-12 gap-3 items-center px-4 py-4 border-b border-gray-800/50 hover:bg-white/5 transition-colors">
      <div className="col-span-3">
        <div className="text-sm font-semibold text-white">{market}</div>
        <div className="text-xs text-gray-500">{createdAt ? new Date(createdAt).toLocaleString() : ""}</div>
      </div>

      <div className="col-span-2">
        <span className={`text-sm font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
          {isUp ? 'Long' : 'Short'}
        </span>
      </div>

      <div className="col-span-2 text-sm text-gray-200">
        ${Number(stake || 0).toLocaleString()}
        {locked !== stake && (
          <span className="text-xs text-gray-500 ml-1">(locked: ${Number(locked || 0).toLocaleString()})</span>
        )}
      </div>
      <div className="col-span-2 text-sm text-gray-300">{dur}s</div>
      <div className="col-span-1 text-sm text-gray-300">{mult}x</div>

      <div className="col-span-2 flex justify-end items-center gap-2">
        <TradeStatusBadge status={status} />
        {note && (
          <span className="text-xs text-gray-400 truncate max-w-[80px]" title={note}>
            {note}
          </span>
        )}
      </div>
    </div>
  );
}

export default function RequestsPage() {
  const [tab, setTab] = useState("PENDING");
  const [status, setStatus] = useState("PENDING");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const limit = pageSize;
  const offset = (page - 1) * pageSize;

  const pendingQ = useTradeRequests({ limit, offset, status });

  const completedQ = useQuery({
    queryKey: ["completedTrades", limit, offset],
    queryFn: () => getCompletedTrades({ limit, offset }),
    enabled: tab === "COMPLETED",
    staleTime: 2000,
  });

  const summaryQ = useQuery({
    queryKey: ["completedTradesSummary"],
    queryFn: () => getCompletedTradesSummary({}),
    enabled: tab === "COMPLETED",
    staleTime: 5000,
  });

  const q = tab === "COMPLETED" ? completedQ : pendingQ;

  const normalized = useMemo(() => normalizeList(q.data, pageSize), [q.data, pageSize]);
  const items = normalized.items || [];
  const total = normalized.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const hasPrev = page > 1;
  const hasNext = page < pageCount;

  const summary = summaryQ.data || null;
  
  // ✅ Compute normalized win rate from summary
  const normalizedWinRate = useMemo(() => {
    return normalizeWinRate(summary?.winRate, summary?.wins, summary?.losses);
  }, [summary?.winRate, summary?.wins, summary?.losses]);

  const getStatusIcon = (tabKey) => {
    if (tabKey === "PENDING") return <Clock size={18} />;
    if (tabKey === "COMPLETED") return <CheckCircle size={18} />;
    return null;
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <ListChecks size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              My Trades
              <span className="text-xs font-medium bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                History
              </span>
            </h1>
            <p className="text-sm text-gray-400">Track all your trade requests and settlements</p>
          </div>
        </div>

        <div className="inline-flex rounded-xl border border-gray-800 bg-gray-900/40 overflow-hidden p-1">
          {[
            { key: "PENDING", label: "Pending", icon: Clock },
            { key: "COMPLETED", label: "Completed", icon: CheckCircle },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setTab(t.key);
                setPage(1);
              }}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                tab === t.key 
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25" 
                  : "text-gray-400 hover:text-white hover:bg-gray-800/50"
              }`}
            >
              <t.icon size={16} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Completed summary - Enhanced */}
      {tab === "COMPLETED" && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-950/40 via-purple-950/30 to-cyan-950/30 border border-indigo-500/20 p-5 mb-6 backdrop-blur-sm">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Award size={16} className="text-indigo-400" />
              <span className="text-sm font-semibold text-white">Performance Summary</span>
            </div>
            {summaryQ.isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                <span className="text-sm text-gray-400">Loading summary...</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Pill tone="good">🏆 Wins: {summary?.wins ?? 0}</Pill>
                <Pill tone="bad">📉 Losses: {summary?.losses ?? 0}</Pill>
                <Pill tone="info">➖ Pushes: {summary?.pushes ?? 0}</Pill>
                <Pill tone="neutral">📊 Trades: {summary?.totalSettled ?? 0}</Pill>
                {/* ✅ FIXED: Use normalized win rate */}
                <Pill tone="good">
                  <span className="flex items-center gap-1">
                    <Target size={12} />
                    Win Rate: {normalizedWinRate.toFixed(1)}%
                  </span>
                </Pill>
                <Pill tone={summary?.netPnL >= 0 ? "good" : "bad"}>
                  <span className="flex items-center gap-1">
                    <Wallet size={12} />
                    Net P&L: ${formatMoney(summary?.netPnL ?? 0)}
                  </span>
                </Pill>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending status filter - Enhanced */}
      {tab === "PENDING" && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {["PENDING", "APPROVED", "REJECTED"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-200 ${
                status === s
                  ? "border-indigo-500/40 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 text-white shadow-lg shadow-indigo-500/10"
                  : "border-gray-800 bg-gray-900/30 text-gray-400 hover:border-gray-700 hover:text-white"
              }`}
            >
              {s === "PENDING" && <Clock size={14} />}
              {s === "APPROVED" && <CheckCircle size={14} />}
              {s === "REJECTED" && <XCircle size={14} />}
              {s === "PENDING" ? "Pending" : s}
            </button>
          ))}
        </div>
      )}

      {/* Main content - Enhanced */}
      {tab === "COMPLETED" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {q.isLoading ? (
            <div className="col-span-2 flex flex-col items-center justify-center py-16 text-gray-400">
              <div className="w-12 h-12 border-3 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4" />
              <span className="text-sm">Loading completed trades...</span>
            </div>
          ) : q.isError ? (
            <div className="col-span-2 text-center py-12 text-red-300 bg-red-500/10 rounded-2xl border border-red-500/20">
              <XCircle size={32} className="mx-auto mb-2" />
              Failed to load: {q.error?.message || "Unknown error"}
            </div>
          ) : items.length === 0 ? (
            <div className="col-span-2 text-center py-16 text-gray-400">
              <div className="text-5xl mb-4">📭</div>
              <p className="text-lg font-medium">No completed trades</p>
              <p className="text-sm text-gray-500 mt-1">Your completed trades will appear here</p>
            </div>
          ) : (
            items.map((r, idx) => <CompletedCard key={r.id || idx} r={r} />)
          )}
        </div>
      ) : (
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 shadow-2xl shadow-black/20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-gray-800/80 bg-gray-900/40 text-xs font-medium text-gray-400">
              <div className="col-span-3">Market</div>
              <div className="col-span-2">Direction</div>
              <div className="col-span-2">Stake</div>
              <div className="col-span-2">Duration</div>
              <div className="col-span-1">Payout</div>
              <div className="col-span-2 text-right">Status</div>
            </div>

            {q.isLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <div className="w-8 h-8 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mr-3" />
                <span className="text-sm">Loading requests...</span>
              </div>
            ) : q.isError ? (
              <div className="p-6 text-red-300 bg-red-500/10 border-t border-red-500/20">
                <XCircle size={16} className="inline mr-2" />
                Failed to load requests: {q.error?.message || "Unknown error"}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <div className="text-5xl mb-4">📭</div>
                <p className="text-lg font-medium">No {status.toLowerCase()} requests</p>
                <p className="text-sm text-gray-500 mt-1">Your pending requests will appear here</p>
              </div>
            ) : (
              items.map((r, idx) => <PendingRow key={r.id || idx} r={r} />)
            )}
          </div>
        </div>
      )}

      {/* Pagination - Enhanced */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-4 mt-6">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 text-gray-300 hover:border-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
            Previous
          </button>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400">
              Page <span className="text-white font-semibold">{page}</span> of {pageCount}
            </span>
            <span className="text-xs text-gray-500">
              {total} total
            </span>
          </div>

          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 text-gray-300 hover:border-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Footer note */}
      <div className="mt-6 text-center text-xs text-gray-500">
        <span className="flex items-center justify-center gap-1">
          <Sparkles size={12} className="text-indigo-400" />
          All trades are recorded on-chain for transparency and security
        </span>
      </div>
    </div>
  );
}
